import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import {
  Modal,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Button,
  TextField,
  LinearProgress
} from '@material-ui/core';
import { Alert } from 'components';

const MAX_CONTENT_LENGTH = 10000;

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 750,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  actions: {
    justifyContent: 'flex-end'
  },
  alert: {
    marginBottom: theme.spacing(2)
  }
}));

const NoteEditor = props => {
  const {
    open,
    note,
    error,
    saving,
    onClose,
    onSave,
    className,
    ...rest
  } = props;

  const classes = useStyles();
  const [content, setContent] = useState('');

  /* Keyed on the note and on opening only, so a failed save leaves what was typed in place */
  useEffect(() => {
    setContent(note ? note.content : '');
  }, [note, open]);

  /* Measured after trimming because that is what the server stores and validates */
  const trimmed = content.trim();
  const tooLong = trimmed.length > MAX_CONTENT_LENGTH;

  const handleSave = () => {
    onSave(trimmed);
  };

  if (!open) {
    return null;
  }

  return (
    <Modal onClose={saving ? undefined : onClose} open={open}>
      <Card {...rest} className={clsx(classes.root, className)}>
        {saving && <LinearProgress />}
        <CardHeader title={note ? 'Edit Note' : 'Add Note'} />
        <Divider />
        <CardContent>
          {error && (
            <Alert className={classes.alert} message={error} variant="error" />
          )}
          <TextField
            autoFocus
            disabled={saving}
            error={tooLong}
            fullWidth
            helperText={
              tooLong
                ? `Notes must be ${MAX_CONTENT_LENGTH} characters or fewer`
                : `${trimmed.length}/${MAX_CONTENT_LENGTH}`
            }
            label="Note"
            multiline
            onChange={event => setContent(event.target.value)}
            rows={8}
            value={content}
            variant="outlined"
          />
        </CardContent>
        <CardActions className={classes.actions}>
          <Button disabled={saving} onClick={onClose} size="small">
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={trimmed === '' || tooLong || saving}
            onClick={handleSave}
            size="small"
            variant="contained">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
};

NoteEditor.propTypes = {
  className: PropTypes.string,
  error: PropTypes.string,
  note: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  open: PropTypes.bool,
  saving: PropTypes.bool
};

NoteEditor.defaultProps = {
  open: false,
  note: null,
  error: null,
  saving: false
};

export default NoteEditor;
